import { useState, useEffect, useCallback, useRef } from 'react';
import { usePageLayout } from '@app/router';
import { useLanguage } from '@shared/contexts/LanguageContext';
import { getWishList, geContentList, addWish, postDescribeWish, getWishCount } from '../api/wishTreeService';
import { TREE_IMAGE, TREE_SLOTS, RINGS_BY_IMAGE_ID, CARD_STYLES } from '../constants';
import WishCard from '../components/WishCard';
import WishFormModal from '../components/WishFormModal';
import WishResultModal from '../components/WishResultModal';
import WishPreviewModal from '../components/WishPreviewModal';
import GoldConfettiBurst from '../components/GoldConfettiBurst';
import './WishTreePage.css';

const SLOT_COUNT = TREE_SLOTS.length;
const RING_COUNT = 7;
const RING_IDS = Array.from({ length: RING_COUNT }, (_, i) => i + 1);
// 依序分配 slot 的順序：每一層（依 ring 由上而下）先排完正面再排背面，
// 確保正面優先被填滿，背面只在該層正面已滿時才輪到。
const FILL_ORDER_INDEXES = RING_IDS.flatMap((ring) => {
    const front = [];
    const back = [];
    TREE_SLOTS.forEach((slot, i) => {
        if (slot.ring !== ring) return;
        (slot.behind ? back : front).push(i);
    });
    return [...front, ...back];
});
// 依卡片顏色（image_id）分色排列：每個顏色各自的填入順序只涵蓋該顏色允許的層級，
// 同樣先正面後背面。既有願望（初次載入）與新掛願望都依此落在對應顏色的圈層。
const FILL_ORDER_BY_IMAGE_ID = CARD_STYLES.reduce((acc, { id }) => {
    const allowedRings = RINGS_BY_IMAGE_ID[id];
    acc[id] = allowedRings
        ? allowedRings.flatMap((ring) => {
            const front = [];
            const back = [];
            TREE_SLOTS.forEach((slot, i) => {
                if (slot.ring !== ring) return;
                (slot.behind ? back : front).push(i);
            });
            return [...front, ...back];
        })
        : FILL_ORDER_INDEXES;
    return acc;
}, {});
const ROTATE_SPEED_DEG_PER_MS = 360 / 45000; // 固定公轉角速度：45 秒一圈，讀取完成即以此速度持續公轉
const COAST_SETTLE_DURATION = 1600; // 鬆手甩動：額外偏移衰減時長
const DRAG_TO_DEG = 0.25; // 拖曳靈敏度（原 0.35）
const MIN_ORBIT_RADIUS = 0.01; // %；單卡或同 x 時視為無公轉半徑
/** 鬆手後慣性：隨機續轉角度範圍與速度增益 */
const INERTIA_MIN_DEG = 90;
const INERTIA_MAX_DEG = 420;
const INERTIA_VELOCITY_GAIN = 320; // |deg/ms| → 額外慣性角度
const INERTIA_SAMPLE_MS = 100; // 取樣視窗
const INERTIA_MIN_VELOCITY = 0.015; // deg/ms；低於此改依隨機／末段方向

const normalizeDeg = (deg) => {
    const m = deg % 360;
    return m > 180 ? m - 360 : m <= -180 ? m + 360 : m;
};

const easeOutCubic = (t) => 1 - (1 - t) ** 3;

/** 各層垂直中心（%），拖曳時用來判斷手指落在哪一層 */
const RING_CENTER_Y = RING_IDS.reduce((acc, ring) => {
    const ys = TREE_SLOTS.filter((s) => s.ring === ring).map((s) => s.y);
    acc[ring] = ys.reduce((a, b) => a + b, 0) / ys.length;
    return acc;
}, {});

/** 以該層目前掛卡的最左～最右為直徑，算出圓心與半徑（各層不同） */
const orbitFromXs = (xs) => {
    if (!xs.length) return { center: 50, radius: 0 };
    const left = Math.min(...xs);
    const right = Math.max(...xs);
    return {
        center: (left + right) / 2,
        radius: (right - left) / 2,
        left,
        right,
    };
};

/** 靜止時落在 slot.x：前方 theta=asin；後方 theta=180-asin（同 x 但在圓柱背面） */
const toRingTheta = (xPercent, orbit, behind = false) => {
    if (!orbit || orbit.radius < MIN_ORBIT_RADIUS) return behind ? 180 : 0;
    const t = Math.max(-1, Math.min(1, (xPercent - orbit.center) / orbit.radius));
    const front = (Math.asin(t) * 180) / Math.PI;
    return behind ? 180 - front : front;
};

const projectOnRing = (xPercent, orbit, rotation, behind = false) => {
    const theta = toRingTheta(xPercent, orbit, behind);
    // rotation 在固定公轉中會持續累加、沒有上限；screenX／depth 靠 sin/cos 本身
    // 就是週期性的，不受影響，但回傳的 liveAngle 會被下游拿去乘上 0.45 算
    // facingY（見 renderRingCards／applyCardStyle）—— 這個乘算不是「360 的倍數
    // 仍等價」的週期性運算，若不先正規化，每多繞一圈時同一個正面位置算出來的
    // facingY 就會多偏移 162°（360*0.45），導致卡片每繞一圈回到正面時朝向角度
    // 都不一樣，看起來像「越轉越歪」。正規化到 (-180,180] 後，同一個視覺位置
    // 永遠對應同一個 liveAngle，facingY 才會是穩定、可重現的。
    const liveAngle = normalizeDeg(theta + rotation);
    const rad = (liveAngle * Math.PI) / 180;
    if (!orbit || orbit.radius < MIN_ORBIT_RADIUS) {
        return { screenX: xPercent, depth: Math.cos(rad), liveAngle };
    }
    return {
        screenX: orbit.center + Math.sin(rad) * orbit.radius,
        depth: Math.cos(rad),
        liveAngle,
    };
};

/**
 * 發願樹主頁面
 * 樹上固定點位依序掛上已送出的發願卡，新卡片送出後飛入隨機空點位並高亮。
 * 七層各自繞該層掛卡左右跨距為直徑的圓柱公轉，固定角速度持續轉動；旋轉只移動
 * 現有卡、不新增。掛卡時只暫停卡片即將落腳的那一層，其他層不受影響。
 */
const WishTreePage = () => {
    const { setLayoutOptions } = usePageLayout();
    const { t, currentLanguage } = useLanguage();
    const isEn = currentLanguage.code === 'en';

    useEffect(() => {
        setLayoutOptions({ noPaddingBottom: true, hideFooter: true, hideBottomNav: true });
        return () => setLayoutOptions({});
    }, [setLayoutOptions]);

    const audioRef = useRef(null);
    const [isMuted, setIsMuted] = useState(true);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.muted = true;
        const playPromise = audio.play();
        if (playPromise?.catch) {
            playPromise.catch(() => { });
        }
    }, []);

    const handleToggleMute = () => {
        const nextMuted = !isMuted;
        const audio = audioRef.current;
        setIsMuted(nextMuted);
        if (!audio) return;
        audio.muted = nextMuted;
        if (!nextMuted) {
            const playPromise = audio.play();
            if (playPromise?.catch) {
                playPromise.catch(() => {
                    setIsMuted(true);
                    audio.muted = true;
                });
            }
        }
    };

    const [wishes, setWishes] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [wishTotal, setWishTotal] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [pendingWish, setPendingWish] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [justPlantedId, setJustPlantedId] = useState(null);
    const [flyingCard, setFlyingCard] = useState(null); // { wish, from:{x,y,w,h}, to:{x,y,w,h}, flying }
    const [previewWish, setPreviewWish] = useState(null);
    const [goldConfetti, setGoldConfetti] = useState(null); // { id, waves: [{waveDelay,spreadDeg,sources}] }
    const GOLD_CONFETTI_DURATION = 10000;

    const stageRef = useRef(null);
    const resultCardRef = useRef(null);
    const contentMapRef = useRef(new Map());
    const FLY_DURATION = 900;

    // 卡片水平公轉用 px 位移（transform）而非 % 的 left，避免每幀觸發 reflow；
    // 換算 % → px 需要容器寬度，用 ResizeObserver 快取起來，不必每幀讀 getBoundingClientRect。
    // 用 offsetWidth（版面寬度，不受 CSS transform 影響）而非 getBoundingClientRect().width
    // （會回傳套用 transform 之後的視覺寬度）：手機版 .wish-tree-stage 有
    // transform: scale(1.25)，若用 getBoundingClientRect 換算出的 px 會被這個
    // scale 污染，疊加上容器自身的縮放後等於放大兩次，導致卡片位置往右偏移跑掉。
    const stageWidthRef = useRef(0);
    useEffect(() => {
        const stageEl = stageRef.current;
        if (!stageEl) return undefined;
        const update = () => { stageWidthRef.current = stageEl.offsetWidth; };
        update();
        const observer = new ResizeObserver(update);
        observer.observe(stageEl);
        return () => observer.disconnect();
    }, []);

    const contentIdToContent = useCallback((contentId) => {
        if (contentId == null) return undefined;
        return contentMapRef.current.get(Number(contentId));
    }, []);

    const resolveWishContent = useCallback((wish) => {
        if (!wish) return wish;
        const content = contentIdToContent(wish.content_id) ?? wish.content;
        return content === wish.content ? wish : { ...wish, content, monastery_name: "" };
    }, [contentIdToContent]);

    // 七層各自獨立的公轉角，只存在 ref：公轉每幀都在變動，若存進 React state
    // 會逼著全部卡片每幀 re-render；改成 RAF 內直接寫卡片 DOM 的 style，
    // React re-render 只在卡片新增／移除等真正需要重新排版時才發生。
    const ringRotationsRef = useRef(Array(RING_COUNT + 1).fill(0));
    /** 鬆手甩動：{ [ring]: { from, startTime, duration } }，額外偏移會以 ease-out
     * 衰減到 0，疊加在固定角速度公轉之上，讓「甩動尾韻」與「持續公轉」之間
     * 沒有速度斷層（不會先減速到停、又瞬間跳回全速）。 */
    const ringBoostRef = useRef({});
    /** 目前暫停固定公轉的層（拖曳中／掛卡飛行中） */
    const frozenRingsRef = useRef(new Set());
    const cardElsRef = useRef(new Map()); // wish_id -> HTMLElement
    const rafRef = useRef(null);
    const lastTickRef = useRef(null);
    /** 指向最新的 applyRingDom（該函式定義在後面，但前面的 handler 需要能呼叫到它） */
    const applyRingDomRef = useRef(() => {});

    /** 讀出某層目前甩動衰減的即時偏移量（平常是 0） */
    const getRingBoost = useCallback((ring, now) => {
        const boost = ringBoostRef.current[ring];
        if (!boost) return 0;
        const progress = Math.min(1, (now - boost.startTime) / boost.duration);
        if (progress >= 1) {
            delete ringBoostRef.current[ring];
            return 0;
        }
        return boost.from * (1 - easeOutCubic(progress));
    }, []);

    /** 立刻停下單一層公轉，角度停在目前顯示角度（base+甩動偏移疊加後的值）；
     * 並凍結該層。回傳凍結時的角度。掛卡飛行期間需要卡位真正不動才用這個。 */
    const stopRingOrbit = useCallback((ring) => {
        const now = performance.now();
        const boostVal = getRingBoost(ring, now);
        const total = (ringRotationsRef.current[ring] ?? 0) + boostVal;
        ringRotationsRef.current[ring] = total;
        delete ringBoostRef.current[ring];
        frozenRingsRef.current.add(ring);
        applyRingDomRef.current(ring, total);
        return total;
    }, [getRingBoost]);

    /** 解凍某層，從目前累積的角度無縫接回固定角速度公轉（不需要重新換算，
     * 因為 ringRotationsRef 這個值本來就沒斷過，只是暫停累加）。 */
    const unfreezeRing = useCallback((ring) => {
        frozenRingsRef.current.delete(ring);
    }, []);

    useEffect(() => {
        geContentList()
            .then((res) => {
                const map = new Map();
                (res?.items ?? []).forEach((item) => {
                    map.set(item.content_ind, item.content);
                });
                contentMapRef.current = map;
            })
            .catch((err) => console.error('Failed to load content list:', err));

        getWishList()
            .then((res) => setWishes(res.items || []))
            .catch((err) => console.error('Failed to load wish list:', err))
            .finally(() => setLoaded(true));

        getWishCount()
            .then((res) => setWishTotal(Number(res?.total) || 0))
            .catch((err) => console.error('Failed to load wish count:', err));
    }, []);

    /** 單一 RAF：未凍結的層持續累加固定角速度；每層再疊加甩動衰減偏移，
     * 兩者相加後直接寫入 DOM，不經過 setState，避免每幀重新 render 全部卡片。 */
    const ensureAnimLoop = useCallback(() => {
        if (rafRef.current) return;

        const tick = (now) => {
            const dt = lastTickRef.current == null ? 0 : now - lastTickRef.current;
            lastTickRef.current = now;

            RING_IDS.forEach((ring) => {
                if (!frozenRingsRef.current.has(ring)) {
                    const direction = ring % 2 === 1 ? -1 : 1;
                    const base = ringRotationsRef.current[ring] ?? 0;
                    ringRotationsRef.current[ring] = base + direction * ROTATE_SPEED_DEG_PER_MS * dt;
                }
                const boostVal = getRingBoost(ring, now);
                const rotation = (ringRotationsRef.current[ring] ?? 0) + boostVal;
                applyRingDomRef.current(ring, rotation);
            });

            rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
    }, [getRingBoost]);

    // 讀取完成、有卡片後立刻以固定角速度公轉；RAF 迴圈本身即為「自動公轉」，
    // 不需額外排程或間隔等待，也不會中途暫停重轉。
    useEffect(() => {
        if (wishes.length === 0) return undefined;
        lastTickRef.current = null;
        ensureAnimLoop();
        return undefined;
    }, [wishes.length, ensureAnimLoop]);

    useEffect(() => () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
    }, []);

    const pickRingByClientY = (clientY) => {
        const stageEl = stageRef.current;
        if (!stageEl) return 1;
        const rect = stageEl.getBoundingClientRect();
        const yPercent = ((clientY - rect.top) / rect.height) * 100;
        let best = 1;
        let bestDist = Infinity;
        RING_IDS.forEach((ring) => {
            const dist = Math.abs(RING_CENTER_Y[ring] - yPercent);
            if (dist < bestDist) {
                bestDist = dist;
                best = ring;
            }
        });
        return best;
    };

    const dragMovedRef = useRef(false);
    /** 拖曳速度取樣：{ x, t }[]，用於鬆手慣性 */
    const dragSamplesRef = useRef([]);
    const draggingRef = useRef(false);
    const dragRingRef = useRef(null);
    const dragStartXRef = useRef(0);
    const dragBaseRotationRef = useRef(0);

    const handleDragStart = (clientX, clientY) => {
        const ring = pickRingByClientY(clientY);
        // 不需等其他層停穩；僅接手這一層。把目前顯示角度（base+甩動偏移）
        // 摺回 base，取消甩動衰減，讓拖曳直接從視覺上當下的角度接手。
        const now = performance.now();
        const boostVal = getRingBoost(ring, now);
        const total = (ringRotationsRef.current[ring] ?? 0) + boostVal;
        ringRotationsRef.current[ring] = total;
        delete ringBoostRef.current[ring];
        frozenRingsRef.current.add(ring);

        draggingRef.current = true;
        dragRingRef.current = ring;
        dragMovedRef.current = false;
        dragStartXRef.current = clientX;
        dragBaseRotationRef.current = total;
        dragSamplesRef.current = [{ x: clientX, t: now }];
    };

    const handleDragMove = (clientX) => {
        if (!draggingRef.current || dragRingRef.current == null) return;
        const ring = dragRingRef.current;
        const deltaX = clientX - dragStartXRef.current;
        if (Math.abs(deltaX) > 6) dragMovedRef.current = true;
        // 不夾限角度，讓圓盤可連續水平公轉；旋轉中不換卡
        const raw = dragBaseRotationRef.current + deltaX * DRAG_TO_DEG;
        ringRotationsRef.current[ring] = raw;
        applyRingDomRef.current(ring, raw);
        const now = performance.now();
        const samples = dragSamplesRef.current;
        samples.push({ x: clientX, t: now });
        while (samples.length > 1 && now - samples[0].t > INERTIA_SAMPLE_MS) {
            samples.shift();
        }
    };

    const handleDragEnd = () => {
        if (!draggingRef.current || dragRingRef.current == null) return;
        const ring = dragRingRef.current;
        draggingRef.current = false;
        dragRingRef.current = null;

        const rot = ringRotationsRef.current[ring] ?? 0;
        const samples = dragSamplesRef.current;
        dragSamplesRef.current = [];

        // 點擊／幾乎沒拖：只正規化，不啟慣性，直接回到固定角速度公轉
        if (!dragMovedRef.current) {
            ringRotationsRef.current[ring] = normalizeDeg(rot);
            unfreezeRing(ring);
            return;
        }

        let velocity = 0; // deg/ms
        if (samples.length >= 2) {
            const first = samples[0];
            const last = samples[samples.length - 1];
            const dt = last.t - first.t;
            if (dt > 0) {
                velocity = ((last.x - first.x) * DRAG_TO_DEG) / dt;
            }
        }

        const lastDeltaX = samples.length >= 2
            ? samples[samples.length - 1].x - samples[0].x
            : 0;
        const dir = Math.abs(velocity) >= INERTIA_MIN_VELOCITY
            ? Math.sign(velocity)
            : (lastDeltaX !== 0 ? Math.sign(lastDeltaX) : (Math.random() < 0.5 ? 1 : -1));

        const velocityCoast = Math.abs(velocity) * INERTIA_VELOCITY_GAIN;
        const randomCoast = INERTIA_MIN_DEG
            + Math.random() * (INERTIA_MAX_DEG - INERTIA_MIN_DEG);
        // 鬆手後隨機續轉，並依甩動速度加碼
        const coastDeg = dir * (randomCoast + velocityCoast);

        // 鬆手當下立刻恢復固定角速度公轉（不必等甩動衰減跑完，「重新開始轉」
        // 是瞬間的），再疊加一段會自然衰減到 0 的額外偏移做出甩動尾韻；
        // ease-out 尾端速度趨近 0，平滑併入固定角速度，沒有頓挫感。
        unfreezeRing(ring);
        ringBoostRef.current[ring] = { from: coastDeg, startTime: performance.now(), duration: COAST_SETTLE_DURATION };
    };

    const handleTouchStart = (e) => handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
    const handleTouchMove = (e) => handleDragMove(e.touches[0].clientX);
    const handleTouchEnd = () => handleDragEnd();

    const handleMouseDown = (e) => handleDragStart(e.clientX, e.clientY);
    const handleMouseMove = (e) => handleDragMove(e.clientX);
    const handleMouseUp = () => handleDragEnd();

    /** 使用者本次掛上的 wish_id；點擊時 recency 固定 1.0 以取回自己的卡 */
    const ownWishIdsRef = useRef(new Set());

    const handleSubmit = useCallback(async (formData) => {
        setSubmitting(true);
        try {
            const result = await addWish(formData);
            // i166 回傳無 wish_id；樹上點位仍需穩定 key，以時間戳補上
            // image_id 以送出時為準，避免回傳缺欄時點擊 describe 對錯卡
            setPendingWish({
                ...result,
                wish_id: result.wish_id ?? Date.now(),
                image_id: result.image_id ?? formData.image_id,
                content_id: result.content_id ?? formData.content_id,
                vower_name: result.vower_name ?? formData.vower_name,
            });
            setShowForm(false);
        } catch (err) {
            console.error('Failed to submit wish:', err);
        } finally {
            setSubmitting(false);
        }
    }, []);

    // 新願望掛進目前樹上可見卡片尚未佔用的點位。
    // slotIndexRef 記錄每個 wish_id 實際佔用的點位序號（0~SLOT_COUNT-1）。
    const slotIndexRef = useRef(new Map());
    // 每個顏色（image_id）依序分配到第幾個（該顏色的）FILL_ORDER 位置，用於既有／
    // 首次載入的願望依顏色分色排列（不含使用者現場掛卡，掛卡走 assignSlotIndex 隨機挑）。
    const colorFillCountRef = useRef(new Map());

    const getVisibleWishes = useCallback((list) => (
        list.slice(Math.max(0, list.length - SLOT_COUNT))
    ), []);

    // 該願望在自己顏色的 FILL_ORDER 序列中依序（先到先得）落點；序列滿了則
    // 退回全域 FILL_ORDER_INDEXES，確保一定能拿到一個有效點位。
    const getSlotIndex = useCallback((wishId, imageId) => {
        if (slotIndexRef.current.has(wishId)) return slotIndexRef.current.get(wishId);
        const order = FILL_ORDER_BY_IMAGE_ID[imageId] ?? FILL_ORDER_INDEXES;
        const count = colorFillCountRef.current.get(imageId) ?? 0;
        colorFillCountRef.current.set(imageId, count + 1);
        const slot = order[count] ?? FILL_ORDER_INDEXES[count] ?? count;
        slotIndexRef.current.set(wishId, slot);
        return slot;
    }, []);

    /** 尚未佔用的正面點位；限定卡片顏色（image_id）對應的層級 ring；
     * 該顏色可掛層級都滿了則回傳該顏色全部正面點位供覆寫。 */
    const getCandidateSlots = useCallback((imageId) => {
        const allowedRings = RINGS_BY_IMAGE_ID[imageId];
        const visible = getVisibleWishes(wishes);
        const usedInBatch = new Set();
        visible.forEach((w) => {
            usedInBatch.add(getSlotIndex(w.wish_id, w.image_id));
        });
        const frontFree = [];
        const frontAll = [];
        for (let s = 0; s < SLOT_COUNT; s += 1) {
            const slot = TREE_SLOTS[s];
            if (slot.behind) continue;
            if (allowedRings && !allowedRings.includes(slot.ring)) continue;
            frontAll.push(s);
            if (!usedInBatch.has(s)) frontFree.push(s);
        }
        if (frontFree.length > 0) return frontFree;
        if (frontAll.length > 0) return frontAll;
        // 該顏色未定義允許層級，或所有層級點位定義有誤：退回全部正面點位
        return Array.from({ length: SLOT_COUNT }, (_, s) => s).filter((s) => !TREE_SLOTS[s].behind);
    }, [wishes, getVisibleWishes, getSlotIndex]);

    /** 隨機挑一個尚未佔用、且屬於該卡片顏色對應層級的點位 */
    const assignSlotIndex = useCallback((wishId, imageId) => {
        const candidates = getCandidateSlots(imageId);
        const chosen = candidates[Math.floor(Math.random() * candidates.length)];
        slotIndexRef.current.set(wishId, chosen);
        return chosen;
    }, [getCandidateSlots]);

    // 掛上願心樹：算出願望即將落腳的樹上點位螢幕座標，讓卡片從結果 popup 的位置
    // 直線飛過去並逐漸縮小，飛行動畫結束後才真正把卡片加入樹上陣列並觸發落地光暈。
    // 只有卡片即將落腳的那一層會暫停公轉，其他層不受影響、繼續轉動。
    const handleConfirmPlant = useCallback(() => {
        if (!pendingWish || draggingRef.current) return;

        const slotIndex = assignSlotIndex(pendingWish.wish_id, pendingWish.image_id);
        const slot = TREE_SLOTS[slotIndex];
        const currentRotation = stopRingOrbit(slot.ring);

        // 該層公轉直徑含即將掛上的這張，與之後 render 一致
        const visible = getVisibleWishes(wishes);
        const xs = [];
        visible.forEach((w) => {
            const s = TREE_SLOTS[getSlotIndex(w.wish_id, w.image_id)];
            if (s.ring === slot.ring) xs.push(s.x);
        });
        xs.push(slot.x);
        const orbit = orbitFromXs(xs);

        // 新卡固定角度 theta 落在 -90°~90°（正面 slot）；若目前圈角度會讓
        // liveAngle 落到背面（|liveAngle| >= 90°），用最小位移把整圈轉回最近的
        // 安全邊界，確保新卡落地當下保證朝向使用者、點得到，其他卡片位移最小。
        const theta = toRingTheta(slot.x, orbit, !!slot.behind);
        const liveAngle = normalizeDeg(theta + currentRotation);
        let nextRotation = currentRotation;
        if (liveAngle >= 90) {
            nextRotation = currentRotation - (liveAngle - 89);
        } else if (liveAngle <= -90) {
            nextRotation = currentRotation + (-89 - liveAngle);
        }
        if (nextRotation !== currentRotation) {
            ringRotationsRef.current[slot.ring] = nextRotation;
            applyRingDomRef.current(slot.ring, nextRotation);
        }

        const stageEl = stageRef.current;
        const cardEl = resultCardRef.current;
        if (!stageEl || !cardEl) {
            unfreezeRing(slot.ring);
            setPendingWish(null);
            return;
        }
        const stageRect = stageEl.getBoundingClientRect();
        const fromRect = cardEl.getBoundingClientRect();

        const { screenX } = projectOnRing(slot.x, orbit, nextRotation, !!slot.behind);
        const slotWidth = stageRect.width * 0.034 * slot.scale;
        const slotHeight = slotWidth * (1241 / 659);

        setFlyingCard({
            wish: pendingWish,
            ring: slot.ring,
            from: { x: fromRect.left, y: fromRect.top, w: fromRect.width, h: fromRect.height },
            to: {
                x: stageRect.left + (stageRect.width * screenX) / 100 - slotWidth / 2,
                y: stageRect.top + (stageRect.height * slot.y) / 100,
                w: slotWidth,
                h: slotHeight,
            },
            flying: false,
        });
        setPendingWish(null);
    }, [
        pendingWish,
        stopRingOrbit,
        assignSlotIndex,
        getVisibleWishes,
        wishes,
        getSlotIndex,
        unfreezeRing,
    ]);

    // flyingCard 掛載後下一幀才切換 flying=true，讓起始樣式先上到 DOM 再觸發 transition
    useEffect(() => {
        if (!flyingCard || flyingCard.flying) return undefined;
        const raf = requestAnimationFrame(() => {
            setFlyingCard((prev) => (prev ? { ...prev, flying: true } : prev));
        });
        return () => cancelAnimationFrame(raf);
    }, [flyingCard]);

    // 飛行動畫結束：真正把願望掛進樹上陣列並觸發落地光暈
    // 光暈持續到下一張新卡掛上去才換人（不自動熄滅）。
    useEffect(() => {
        if (!flyingCard || !flyingCard.flying) return undefined;
        const timer = setTimeout(() => {
            const { wish, ring } = flyingCard;
            ownWishIdsRef.current.add(wish.wish_id);
            setWishes((prev) => [...prev, wish]);
            setJustPlantedId(wish.wish_id);
            // 樹底部多點、分波次往上噴發彩帶，在 10 秒視窗內慢慢接力施放（此起彼落，
            // 節奏放緩而非密集連發）：每波固定左、右兩點一起噴（真實拉炮左右各一把
            // 的感覺），中間點位視情況隨機加入 0~2 個，角度範圍窄寬交替。
            const stageEl = stageRef.current;
            if (stageEl) {
                const rect = stageEl.getBoundingClientRect();
                const sourceY = rect.top + rect.height * 0.92;
                const toSources = (fxList) => fxList.map((fx) => ({
                    x: rect.left + rect.width * fx,
                    y: sourceY,
                }));
                const WAVE_COUNT = 5;
                const WAVE_WINDOW_MS = 5300; // 最後一波啟動後還要留給自身動畫時長（最長約 4.2s）跑完，整體控制在 10 秒內結束
                const waves = Array.from({ length: WAVE_COUNT }, (_, wi) => {
                    const left = 0.1 + Math.random() * 0.08; // 左側固定區間，微幅隨機避免每波完全重疊
                    const right = 0.82 + Math.random() * 0.08; // 右側固定區間
                    const midCount = Math.random() < 0.5 ? (Math.random() < 0.5 ? 1 : 2) : 0;
                    const mids = Array.from({ length: midCount }, () => 0.38 + Math.random() * 0.24);
                    const fxList = [left, ...mids, right].sort((a, b) => a - b);
                    return {
                        waveDelay: Math.round((wi / WAVE_COUNT) * WAVE_WINDOW_MS + Math.random() * 400),
                        spreadDeg: 28 + Math.random() * 30,
                        sources: toSources(fxList),
                    };
                });
                setGoldConfetti({ id: wish.wish_id, waves });
            }
            setFlyingCard(null);
            unfreezeRing(ring);
        }, FLY_DURATION);
        return () => clearTimeout(timer);
    }, [flyingCard, unfreezeRing]);

    // 彩帶噴發播完後自動移除，不需要跟落地光暈（justPlantedId）共用生命週期，
    // 光暈要留到下一張新卡掛上才換人，噴發則是一次性動畫。
    useEffect(() => {
        if (!goldConfetti) return undefined;
        const timer = setTimeout(() => setGoldConfetti(null), GOLD_CONFETTI_DURATION);
        return () => clearTimeout(timer);
    }, [goldConfetti]);

    // 目前樹上顯示的卡片；旋轉只移動它們，不在過程中換入新卡
    const displayWishes = getVisibleWishes(wishes);

    // 各層以「目前掛上的最左～最右」為公轉直徑（各層不同）；同時把卡片依層分組，
    // 讓 RAF 每幀不必重新掃過全部卡片再篩選 ring —— 137 張卡若每幀都對 7 層各掃一次
    // 找出屬於該層的卡片，等於每幀近千次疊代。這裡分組結果存進 ref，只在
    // wishes 變動時重新計算一次。
    const { ringOrbits, cardsByRing } = (() => {
        const xsByRing = RING_IDS.reduce((acc, ring) => {
            acc[ring] = [];
            return acc;
        }, {});
        const byRing = RING_IDS.reduce((acc, ring) => {
            acc[ring] = [];
            return acc;
        }, {});
        displayWishes.forEach((wish, i) => {
            const slot = TREE_SLOTS[getSlotIndex(wish.wish_id, wish.image_id)];
            xsByRing[slot.ring].push(slot.x);
            byRing[slot.ring].push({ wish, slot, i });
        });
        const orbits = RING_IDS.reduce((acc, ring) => {
            acc[ring] = orbitFromXs(xsByRing[ring]);
            return acc;
        }, {});
        return { ringOrbits: orbits, cardsByRing: byRing };
    })();
    const ringOrbitsRef = useRef(ringOrbits);
    ringOrbitsRef.current = ringOrbits;
    const cardsByRingRef = useRef(cardsByRing);
    cardsByRingRef.current = cardsByRing;

    /** 把單張卡片依目前公轉角度直接寫進 DOM style，繞過 React re-render。
     * 水平位置寫 --slot-x（px，CSS 用 translate3d 消費）而非 left（%）：純 transform
     * 只觸發 GPU 合成，不會逼瀏覽器對其他卡片重新做 layout（reflow）。疊放順序
     * 完全交給這裡算出的 zIndex 決定（flat DOM，非 preserve-3d），確保點擊命中
     * 判定跟視覺疊放順序一致。 */
    const applyCardStyle = useCallback((el, slot, ring, rotation, orbit) => {
        if (!el) return;
        const { screenX, depth, liveAngle } = projectOnRing(slot.x, orbit, rotation, !!slot.behind);
        const facingY = -liveAngle * 0.45;
        el.style.setProperty('--slot-x', `${(screenX / 100) * stageWidthRef.current}px`);
        el.style.setProperty('--slot-wrap-y', `${facingY}deg`);
        // depth 精度需夠高，避免捨入誤差讓不同深度的卡片撞到同一個 zIndex
        // （轉到前面卻仍被別張卡蓋住）；ring 只作次要排序，主排序永遠是 depth。
        el.style.zIndex = Math.round((depth + 1) * 100000) + ring;
        el.style.pointerEvents = depth > 0 ? 'auto' : 'none';
    }, []);

    /** 重新畫某一層所有卡片的位置（RAF tick／拖曳／掛卡都呼叫這個，不觸發 React re-render）。
     * 只走該層預先分組好的卡片清單，不重新掃過全部卡片再篩選 ring。 */
    const applyRingDom = useCallback((ring, rotation) => {
        const orbit = ringOrbitsRef.current[ring];
        cardsByRingRef.current[ring].forEach(({ wish, slot }) => {
            applyCardStyle(cardElsRef.current.get(wish.wish_id), slot, ring, rotation, orbit);
        });
    }, [applyCardStyle]);
    applyRingDomRef.current = applyRingDom;

    // 卡片清單變動（新卡掛上／首次載入）後，各層公轉直徑可能跟著變，
    // 重新整批寫一次 DOM 位置，確保既有卡片也同步到新的圓心／半徑。
    useEffect(() => {
        RING_IDS.forEach((ring) => {
            applyRingDomRef.current(ring, ringRotationsRef.current[ring] ?? 0);
        });
    }, [wishes]);

    // 畫某一層卡片：繞該層自己的圓心／半徑公轉。
    // sin → 水平位移（最左↔正中↔最右），cos → 前後深度；背面被前面蓋住。
    // 這裡只算首次掛載用的初始樣式；旋轉時改由 applyRingDom 直接寫 DOM，不會觸發這裡重跑。
    const renderRingCards = (ring, rotation, orbit) => (
        cardsByRing[ring].map(({ wish, slot, i }) => {
            const isNew = wish.wish_id === justPlantedId;
            const { screenX, depth, liveAngle } = projectOnRing(slot.x, orbit, rotation, !!slot.behind);
            const facingY = -liveAngle * 0.45;

            return (
                <div
                    key={wish.wish_id}
                    ref={(el) => {
                        if (el) cardElsRef.current.set(wish.wish_id, el);
                        else cardElsRef.current.delete(wish.wish_id);
                    }}
                    className={`wish-tree-slot${isNew ? ' wish-tree-slot--new' : ''}`}
                    style={{
                        top: `${slot.y}%`,
                        '--slot-x': `${(screenX / 100) * stageWidthRef.current}px`,
                        '--slot-scale': slot.scale,
                        '--slot-rotate': `${slot.rotate}deg`,
                        '--slot-wrap-y': `${facingY}deg`,
                        animationDelay: isNew ? '0ms' : `${i * 90}ms`,
                        // depth 精度需夠高，避免捨入誤差讓不同深度的卡片撞到同一個 zIndex
                        // （轉到前面卻仍被別張卡蓋住）；ring 只作次要排序，主排序永遠是 depth。
                        zIndex: Math.round((depth + 1) * 100000) + ring,
                        pointerEvents: depth > 0 ? 'auto' : 'none',
                        cursor: 'pointer',
                    }}
                    onClick={async () => {
                        if (dragMovedRef.current) return;
                        // 自己掛的卡沒有唯一 record_id 可查，且送出當下的資料已完整，
                        // 直接顯示本地資料，避免用 image_id+recency 誤抓到別人同圖樣的卡。
                        if (ownWishIdsRef.current.has(wish.wish_id)) {
                            setPreviewWish(resolveWishContent(wish));
                            return;
                        }
                        try {
                            const result = await postDescribeWish({
                                image_id: wish.image_id,
                                recency: Math.random(),
                            });
                            setPreviewWish(resolveWishContent({ ...wish, ...result }));
                        } catch (err) {
                            console.error('Failed to describe wish:', err);
                        }
                    }}
                >
                    <WishCard wish={wish} size="slot" />
                </div>
            );
        })
    );

    // 所有圈的卡片攤平在同一層渲染（不再各自包一層 .wish-tree-ring 容器），
    // 讓每張卡片的 zIndex 能跨圈全域比較：轉到前面的卡片一定蓋過其他圈轉到後面的卡片。
    const renderAllRings = () => (
        <div className="wish-tree-batch">
            {loaded && RING_IDS.flatMap((ring) => {
                const rotation = ringRotationsRef.current[ring] ?? 0;
                return renderRingCards(ring, rotation, ringOrbits[ring]);
            })}
        </div>
    );

    return (
        <div className={`wish-tree-page${isEn ? ' wish-tree-page--en' : ''}`}>
            <audio ref={audioRef} src="/music/wish_tree.mp3" loop muted autoPlay playsInline preload="metadata" />

            <button
                type="button"
                onClick={handleToggleMute}
                aria-label={isMuted ? t('wish-tree-page.unmuteAria') : t('wish-tree-page.muteAria')}
                className="fixed bottom-20 md:bottom-5 right-3 z-30 flex h-10 w-10 items-center justify-center rounded-full text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)' }}
            >
                <span className="material-symbols-outlined text-xl leading-none">
                    {isMuted ? 'volume_off' : 'volume_up'}
                </span>
            </button>

            <div className="wish-tree-bg" />
            <div className="wish-tree-ground" />

            <div className={`wish-tree-header${isEn ? ' wish-tree-header--en' : ''}`}>
                <h1 className={`wish-tree-header-title${isEn ? ' wish-tree-header-title--en' : ''}`}>{t('wish-tree-page.title')}</h1>
                <p className={`wish-tree-header-desc${isEn ? ' wish-tree-header-desc--en' : ''}`}>
                    {t('wish-tree-page.description')}
                </p>
                {wishTotal != null && wishTotal >= 1000 && (
                    <p className="wish-tree-header-count">
                        {t('wish-tree-page.totalCount')}<span className="wish-tree-header-count-num">{wishTotal}</span>
                    </p>
                )}
            </div>

            <div className="wish-tree-stage" ref={stageRef}>
                {!loaded && (
                    <div className="wish-tree-loading">
                        <span className="material-symbols-outlined animate-spin text-4xl text-[var(--color-primary)]">
                            progress_activity
                        </span>
                    </div>
                )}
                {loaded && (
                    <div
                        className="wish-tree-sway"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                    >
                        <img src={TREE_IMAGE} alt={t('wish-tree-page.treeAlt')} className="wish-tree-image" draggable={false} />

                        {renderAllRings()}
                    </div>
                )}
            </div>

            <button
                onClick={() => setShowForm(true)}
                className={`wish-tree-makeVow fixed ${isEn ? 'top-75' : 'top-60'} right-3 z-30 w-16 h-16 md:w-20 md:h-20 text-white rounded-full hover:shadow-xl transition-all hover:scale-105 flex flex-col items-center justify-center`}
            >
                <span className="text-xs md:text-sm font-medium leading-tight">{t('wish-tree-page.makeVow')}</span>
            </button>

            {showForm && (
                <WishFormModal
                    onClose={() => setShowForm(false)}
                    onSubmit={handleSubmit}
                    submitting={submitting}
                />
            )}

            {pendingWish && (
                <WishResultModal wish={pendingWish} onConfirm={handleConfirmPlant} cardRef={resultCardRef} />
            )}

            {previewWish && (
                <WishPreviewModal wish={previewWish} onClose={() => setPreviewWish(null)} />
            )}

            {flyingCard && (
                <div
                    className="wish-tree-flying-card"
                    style={{
                        left: `${(flyingCard.flying ? flyingCard.to : flyingCard.from).x}px`,
                        top: `${(flyingCard.flying ? flyingCard.to : flyingCard.from).y}px`,
                        width: `${(flyingCard.flying ? flyingCard.to : flyingCard.from).w}px`,
                        height: `${(flyingCard.flying ? flyingCard.to : flyingCard.from).h}px`,
                        transitionDuration: `${FLY_DURATION}ms`,
                    }}
                >
                    <WishCard wish={flyingCard.wish} size="slot" style={{ width: '100%', height: '100%' }} />
                </div>
            )}

            {goldConfetti && <GoldConfettiBurst key={goldConfetti.id} waves={goldConfetti.waves} />}
        </div>
    );
};

export default WishTreePage;
